import { useEffect, useMemo, useState } from 'react'
import { Layout, Typography, Spin } from 'antd'
import { ClockCircleOutlined, RightOutlined, HistoryOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { apiBaseURL } from '../../utils/api'
import { useAppDispatch, useAppSelector } from '../../utils/redux/store'
import { attemptGetPlaylistHistory } from '../../utils/redux/playlistHistorySlice'
import SearchInput from '../../components/SearchInput/SearchInput'

import './PlaylistHistory.scss'

const { Content } = Layout
const { Title, Text } = Typography

type ThemeMode = 'light' | 'dark'

function detectThemeMode(): ThemeMode {
  if (typeof document === 'undefined') return 'dark'

  const html = document.documentElement
  const body = document.body

  const attrs = [
    body.getAttribute('data-theme'),
    html.getAttribute('data-theme'),
    body.getAttribute('theme'),
    html.getAttribute('theme'),
  ]
    .filter(Boolean)
    .map((v) => String(v).toLowerCase())

  let storageTheme = ''
  try {
    storageTheme = String(localStorage.getItem('theme') || '').toLowerCase()
  } catch {
    // noop
  }

  const isLightAttr = attrs.some((v) => v.includes('light'))
  const isLightClass =
    body.classList.contains('light') ||
    html.classList.contains('light') ||
    body.classList.contains('theme-light') ||
    html.classList.contains('theme-light')

  const isLightStorage = storageTheme.includes('light')

  return isLightAttr || isLightClass || isLightStorage ? 'light' : 'dark'
}

function getPlaylistImage(image?: string) {
  if (!image) return ''
  return `${apiBaseURL}/uploaded/playlists/${image}`
}

function getLocale(language?: string) {
  const short = String(language || 'en')
    .split('-')[0]
    .toLowerCase()

  switch (short) {
    case 'uk':
      return 'uk-UA'
    case 'fr':
      return 'fr-FR'
    case 'ru':
      return 'ru-RU'
    case 'de':
      return 'de-DE'
    case 'es':
      return 'es-ES'
    case 'it':
      return 'it-IT'
    default:
      return 'en-US'
  }
}

function getCopy(langShort: string) {
  switch (langShort) {
    case 'uk':
      return {
        history: 'Історія',
        subtitle: 'Нещодавно прослухані плейлисти',
        played: 'Прослухано',
        noHistoryTitle: 'Історія поки порожня',
        noHistoryDesc: 'Тут з’являться ваші нещодавно прослухані плейлисти.',
        playlists: 'плейлистів',
      }
    case 'fr':
      return {
        history: 'Historique',
        subtitle: 'Playlists écoutées récemment',
        played: 'Écouté',
        noHistoryTitle: "Aucun historique pour l'instant",
        noHistoryDesc: 'Vos playlists récemment écoutées apparaîtront ici.',
        playlists: 'playlists',
      }
    case 'ru':
      return {
        history: 'История',
        subtitle: 'Недавно прослушанные плейлисты',
        played: 'Прослушано',
        noHistoryTitle: 'История пока пуста',
        noHistoryDesc: 'Здесь появятся ваши недавно прослушанные плейлисты.',
        playlists: 'плейлистов',
      }
    default:
      return {
        history: 'History',
        subtitle: 'Recently played playlists',
        played: 'Played',
        noHistoryTitle: 'No history yet',
        noHistoryDesc: 'Your recently played playlists will appear here.',
        playlists: 'playlists',
      }
  }
}

function getSongsLabel(count: number, langShort: string) {
  switch (langShort) {
    case 'uk': {
      const mod10 = count % 10
      const mod100 = count % 100

      if (mod10 === 1 && mod100 !== 11) return 'пісня'
      if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) {
        return 'пісні'
      }
      return 'пісень'
    }

    case 'ru': {
      const mod10 = count % 10
      const mod100 = count % 100

      if (mod10 === 1 && mod100 !== 11) return 'песня'
      if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) {
        return 'песни'
      }
      return 'песен'
    }

    case 'fr':
      return count > 1 ? 'titres' : 'titre'

    default:
      return count === 1 ? 'song' : 'songs'
  }
}

function getMinutesShort(langShort: string) {
  switch (langShort) {
    case 'uk':
      return 'хв'
    case 'ru':
      return 'мин'
    default:
      return 'min'
  }
}

function getPlaylistTitleInitial(title?: string) {
  const value = String(title || '').trim()
  return value ? value[0].toUpperCase() : 'P'
}

export default function PlaylistHistory() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { t, i18n } = useTranslation('common')
  const { items, isLoading } = useAppSelector((state) => state.playlistHistory)

  const [themeMode, setThemeMode] = useState<ThemeMode>('dark')

  const langShort = useMemo(
    () =>
      String(i18n.language || 'en')
        .split('-')[0]
        .toLowerCase(),
    [i18n.language],
  )

  const locale = useMemo(() => getLocale(i18n.language), [i18n.language])
  const copy = useMemo(() => getCopy(langShort), [langShort])

  useEffect(() => {
    dispatch(attemptGetPlaylistHistory(20))
  }, [dispatch])

  useEffect(() => {
    const apply = () => setThemeMode(detectThemeMode())
    apply()

    if (typeof document === 'undefined') return

    const html = document.documentElement
    const body = document.body

    const observer = new MutationObserver(() => apply())

    observer.observe(html, {
      attributes: true,
      attributeFilter: ['class', 'data-theme', 'theme'],
    })

    observer.observe(body, {
      attributes: true,
      attributeFilter: ['class', 'data-theme', 'theme'],
    })

    return () => observer.disconnect()
  }, [])

  const formatPlayedAt = (timestamp: number) => {
    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(timestamp))
  }

  return (
    <Content className={`ph-page ph-page--${themeMode}`}>
      <div className="ph-top">
        <SearchInput />
      </div>

      <div className="ph-shell">
        <section className="ph-hero">
          <div className="ph-heroContent">
            <div className="ph-kicker">
              {t('playlistHistory', {
                defaultValue: copy.subtitle,
              })}
            </div>

            <Title level={2} className="ph-title">
              {t('history', { defaultValue: copy.history })}
            </Title>

            <Text className="ph-subtitle">
              {t('playlistHistory', {
                defaultValue: copy.subtitle,
              })}
            </Text>
          </div>

          <div className="ph-heroStats">
            <div className="ph-stat">
              <span className="ph-statValue">{items.length}</span>
              <span className="ph-statLabel">{copy.playlists}</span>
            </div>
          </div>
        </section>

        {isLoading ? (
          <div className="ph-loading">
            <Spin size="large" />
          </div>
        ) : items.length === 0 ? (
          <div className="ph-emptyWrap">
            <div className="ph-empty">
              <div className="ph-emptyIcon">
                <HistoryOutlined />
              </div>

              <div className="ph-emptyTitle">
                {t('noPlaylistHistoryYet', {
                  defaultValue: copy.noHistoryTitle,
                })}
              </div>

              <div className="ph-emptyDesc">{copy.noHistoryDesc}</div>
            </div>
          </div>
        ) : (
          <div className="ph-grid">
            {items.map((item) => {
              const title = item.playlist?.title || ''
              const image = getPlaylistImage(item.playlist?.image)
              const songsCount = Number(item.playlist?.songsCount || 0)
              const totalMinutes = Math.round(
                Number(item.playlist?.totalDuration || 0) / 60,
              )

              return (
                <button
                  key={`${item.playlist._id}-${item.listenedAt}`}
                  type="button"
                  className="ph-card"
                  onClick={() => navigate(`/playlist/${item.playlist._id}`)}
                >
                  <div className="ph-coverWrap">
                    {image ? (
                      <img className="ph-cover" src={image} alt={title} loading="lazy" />
                    ) : (
                      <div className="ph-coverFallback">
                        {getPlaylistTitleInitial(title)}
                      </div>
                    )}
                  </div>

                  <div className="ph-content">
                    <div className="ph-row">
                      <div className="ph-nameWrap">
                        <div className="ph-name">{title}</div>
                      </div>

                      <div className="ph-cardArrow">
                        <RightOutlined />
                      </div>
                    </div>

                    <div className="ph-meta">
                      {songsCount > 0 && (
                        <span className="ph-chip">
                          {songsCount} {getSongsLabel(songsCount, langShort)}
                        </span>
                      )}

                      {totalMinutes > 0 && (
                        <span className="ph-chip">
                          {totalMinutes} {getMinutesShort(langShort)}
                        </span>
                      )}
                    </div>

                    <div className="ph-playedAt">
                      <ClockCircleOutlined />
                      <span>
                        {t('playedAt', { defaultValue: copy.played })}:{' '}
                        {formatPlayedAt(item.listenedAt)}
                      </span>
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </div>
    </Content>
  )
}
